import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Row,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import trTR from 'antd/es/locale/tr_TR';
import { getProgressPaymentTemplateByStore } from '../../../../store/requests/progressPayment';
import { useTranslation } from 'react-i18next';
import { ORDER_TYPES } from '../../../../helpers/constants';
import { DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { PAGE_URLS } from 'helpers/endpoints';
import userHaveRolePermission from 'helpers/userHaveRolePermission';

const Filter = lazy(() => import('./Filter'));

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 20,
  },
};

const ProgressPaymentTemplateByStore = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const csvLink = useRef();
  const [loading, setLoading] = useState(false);
  const [filteringValue, setFilteringValue] = useState({});
  const [tableParams, setTableParams] = useState(initialPageParams);
  const [filterOpen, setFilterOpen] = useState(false);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [downloadedData, setDownloadedData] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData(tableParams, filteringValue);
    return () => {
      setRows([]);
    };
  }, []);

  const columns = [
    {
      title: t('cityName'),
      dataIndex: 'cityName',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('district'),
      dataIndex: 'districtName',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('region'),
      dataIndex: 'regionName',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('storeCode'),
      dataIndex: 'storeCode',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('storeName'),
      dataIndex: 'storeName',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('fixedVestingTemplateName'),
      dataIndex: 'progressPaymentTemplateName',
      sorter: true,
      showSorterTooltip: false,
    },
  ];

  const FILTER_FIELD_MAP = {
    storeCode: 'store.code',
    progressPaymentTemplateName: 'progressPaymentTemplate.name',
  };

  const buildFilterParts = (filter) => {
    return Object.entries(filter)
      .filter(
        ([, value]) => value != null && value !== undefined && value !== '',
      )
      .map(([key, value]) => {
        const field = FILTER_FIELD_MAP[key] || key;
        const safeValue = String(value).replace(/'/g, "\\'");
        return `${field}=='${safeValue}'`;
      });
  };

  const buildQueryString = (tableParamsValue, filter) => {
    const filterParts = buildFilterParts(filter);

    let queryString = '';
    if (filterParts.length > 0) {
      queryString += `filter=${filterParts.join(' and ')}`;
    }
    queryString += `${queryString ? '&' : ''}page=${tableParamsValue.pagination.current - 1}&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue.column) {
      queryString += `&sort=${tableParamsValue.column.dataIndex},${ORDER_TYPES[tableParamsValue.order]}`;
    }

    return queryString;
  };

  const fetchData = (tableParamsValue, filter) => {
    const queryString = buildQueryString(tableParamsValue, filter);
    setLoading(true);
    getProgressPaymentTemplateByStore(queryString)
      .then((response) => {
        const { content, page } = response;
        setRows(content || []);
        setTableParams({
          ...tableParamsValue,
          pagination: {
            ...tableParamsValue.pagination,
            total: page.totalElements,
            current: page.number + 1,
            pageSize: page.size,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const newTableParams = { ...tableParams, pagination, ...sorter };
    setTableParams(newTableParams);
    fetchData(newTableParams, filteringValue);
  };

  const handlerFilter = (values) => {
    const newFilteringValue = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v != null && v !== ''),
    );
    setFilteringValue(newFilteringValue);
    const resetTableParams = {
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1 },
    };
    setTableParams(resetTableParams);
    setFilterOpen(false);
    fetchData(resetTableParams, newFilteringValue);
  };

  const getFilterLabel = (key) => {
    const labelMap = {
      cityName: t('cityName'),
      districtName: t('district'),
      regionName: t('region'),
      storeCode: t('storeCode'),
      storeName: t('storeName'),
      progressPaymentTemplateName: t('fixedVestingTemplateName'),
    };
    return labelMap[key] || t(key);
  };

  const renderSelectedFilters = () => {
    return Object.keys(filteringValue).map((item, idx) => {
      if (filteringValue[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${getFilterLabel(item)} : ${filteringValue[item]}`}
          </Tag>
        );
      }
      return null;
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    const editedFilters = {
      ...filteringValue,
      [destinationItem]: undefined,
    };
    const cleanedFilters = Object.fromEntries(
      Object.entries(editedFilters).filter(([, v]) => v != null && v !== ''),
    );
    setFilteringValue(cleanedFilters);
    form.setFieldsValue({ ...editedFilters });
    const resetTableParams = {
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1 },
    };
    setTableParams(resetTableParams);
    fetchData(resetTableParams, cleanedFilters);
  };

  const handleExport = async () => {
    setExportButtonLoading(true);
    try {
      const filterParts = buildFilterParts(filteringValue);
      let queryString = '';
      if (filterParts.length > 0) {
        queryString += `filter=${filterParts.join(' and ')}&`;
      }
      queryString += `page=0&size=20000`;
      const response = await getProgressPaymentTemplateByStore(queryString);
      const { content } = response;
      if (content && content.length > 0) {
        const csvData = [
          columns.map((col) => col.title),
          ...content.map((item) => [
            item.cityName,
            item.districtName,
            item.regionName,
            item.storeCode,
            item.storeName,
            item.progressPaymentTemplateName,
          ]),
        ];
        setDownloadedData(csvData);
        csvLink.current.link.click();
      }
    } finally {
      setExportButtonLoading(false);
    }
  };

  return (
    <ConfigProvider locale={trTR}>
      <Row justify="space-between" align="middle">
        <Col>
          <Tooltip title={t('download')}>
            <Button
              shape="round"
              icon={<DownloadOutlined />}
              style={{ marginRight: 4 }}
              loading={exportButtonLoading}
              onClick={handleExport}
            />
          </Tooltip>
          <CSVLink
            filename={'MagazaHakedisEslestirmeleri.csv'}
            data={downloadedData}
            ref={csvLink}
            className="hidden"
            separator=";"
            uFEFF={true}
          />
        </Col>
        <Col>
          <Tooltip title={t('filter')}>
            <Button
              shape="round"
              icon={<FilterOutlined />}
              onClick={() => setFilterOpen(true)}
            >
              {t('filters')}
            </Button>
          </Tooltip>
        </Col>
      </Row>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '10px',
          marginBottom: '10px',
        }}
      >
        {renderSelectedFilters()}
      </Row>
      <Table
        rowKey={(record) => record.id}
        dataSource={rows}
        columns={columns}
        loading={loading}
        onChange={handleTableChange}
        pagination={tableParams.pagination}
        scroll={{ x: 'max-content' }}
        size="small"
      />
      <Suspense fallback={null}>
        {filterOpen && (
          <Filter
            form={form}
            filterOpen={filterOpen}
            setIsFiltersOpen={setFilterOpen}
            handlerFilter={handlerFilter}
          />
        )}
      </Suspense>
    </ConfigProvider>
  );
};

export default ProgressPaymentTemplateByStore;
